import type {
  OrchestratedCommonListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'

import { useSellerCollectsShipping } from '@/buyer-portal/components/features/listing/Shipment/useShipmentDetails'

/**
 * Whether the Total Landed Cost breakdown should show no shipping figure.
 *
 * On a Seller Collects lot B-Stock only quotes freight it books itself, so for
 * every other mode a pickup quote of `totalPrice: 0` would otherwise render as
 * "$0.00" — a cost the page elsewhere says B-Stock does not collect.
 *
 * The storefront mode alone is not enough. A Seller Collects listing may also
 * offer Standard Shipping, and a buyer who picks that BINDING quote has a real
 * freight cost which the total already includes; dashing the line there hides
 * a charge rather than declining to invent one. So a selected quote
 * carrying an amount always wins over the mode.
 *
 * Shared by the bid card, the offer card and the confirmation modals so none
 * of them can disagree. `useSellerCollectsShipping` already gates on the
 * feature flag, so this does too.
 */
export const useSuppressShippingLine = (
  listing: OrchestratedCommonListingDetails | undefined,
  selectedQuote?: OrchestratedQuote | null
): boolean => {
  const { isSellerCollects, showQuotedAmount } =
    useSellerCollectsShipping(listing)

  // Zero is "no amount quoted" here, which is the pickup case this suppresses.
  const hasQuotedAmount = Boolean(selectedQuote?.totalPrice)

  return isSellerCollects && !showQuotedAmount && !hasQuotedAmount
}

/**
 * Whether the "Shipping & Other Charges" total should show no figure either.
 *
 * The total is the shipping row plus B-Stock's own fee. With the shipping row
 * dashed and no fee to charge, a "$0.00" header reads as "B-Stock has priced
 * this and it is free" directly above a row that says the opposite — the same
 * claim {@link useSuppressShippingLine} exists to stop, one level up. Where
 * there is a fee the header still states it: that money is owed, and dashing
 * it would hide a charge rather than decline to invent one.
 */
export const suppressChargesTotal = (
  suppressShippingLine: boolean,
  totalCents: number
): boolean => suppressShippingLine && totalCents === 0
